import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

interface DataTablePaginationProps {
  /** 1-based current page. */
  page: number;
  /** Rows per page currently in effect. */
  pageSize: number;
  /** Total matching rows across every page. */
  total: number;
  /** Called with the next 1-based page. */
  onPageChange: (page: number) => void;
  /**
   * Called when the user picks a new page size. Omit to hide the
   * size selector — useful when the page is locked to a single size
   * (e.g. a dashboard widget).
   */
  onPageSizeChange?: (pageSize: number) => void;
  /** Override the default `[10, 20, 50, 100]` size options. */
  pageSizeOptions?: readonly number[];
  /** Singular noun for the count label (e.g. `'organization'` → `1 of 42`). */
  itemLabel?: string;
  className?: string;
  /** Disable every control while data is in flight. */
  isLoading?: boolean;
}

/**
 * Standard offset-based pagination footer for any list rendered as a
 * table or grid. Pairs with a server endpoint that accepts `page` +
 * `pageSize` and returns `{ items, total, totalPages }` (see
 * `paginatedResponseSchema` in `@reflex/shared`).
 *
 * Layout: left = count summary, right = page-size selector + jump
 * controls. The component is purely presentational — owning state in
 * the URL (via `nuqs`) or React Query keeps the pagination shareable
 * and back-button-friendly; this primitive just renders the controls.
 */
export function DataTablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  itemLabel,
  className,
  isLoading,
}: DataTablePaginationProps) {
  const totalPages = total === 0 ? 0 : Math.ceil(total / pageSize);
  const safePage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);
  const canPrev = safePage > 1 && !isLoading;
  const canNext = safePage < totalPages && !isLoading;
  const pluralizedLabel = itemLabel && total !== 1 ? `${itemLabel}s` : itemLabel;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-t border-border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:py-3',
        className,
      )}
      data-testid="data-table-pagination"
    >
      <div className="flex items-center justify-between gap-2 sm:contents">
        <p className="text-xs text-muted-foreground" data-testid="data-table-pagination-summary">
          {total === 0
            ? `No ${pluralizedLabel ?? 'results'}`
            : `${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}${
                pluralizedLabel ? ` ${pluralizedLabel}` : ''
              }`}
        </p>

        {onPageSizeChange && (
          <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground sm:order-3">
            <span className="max-sm:sr-only">Rows</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
              disabled={isLoading}
            >
              <SelectTrigger
                size="sm"
                className="h-7 w-[4.25rem] px-2 text-xs"
                aria-label="Rows per page"
                data-testid="data-table-pagination-size"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        )}
      </div>

      <div className="flex items-center justify-center gap-1 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className="max-sm:hidden"
          aria-label="First page"
          onClick={() => onPageChange(1)}
          disabled={!canPrev}
          data-testid="data-table-pagination-first"
        >
          <ChevronsLeft />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Previous page"
          onClick={() => onPageChange(safePage - 1)}
          disabled={!canPrev}
          data-testid="data-table-pagination-prev"
        >
          <ChevronLeft />
        </Button>
        <span
          className="min-w-[5.5rem] px-1 text-center text-xs tabular-nums text-muted-foreground sm:min-w-[6.5rem]"
          data-testid="data-table-pagination-page"
        >
          Page {safePage} of {Math.max(totalPages, 1)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Next page"
          onClick={() => onPageChange(safePage + 1)}
          disabled={!canNext}
          data-testid="data-table-pagination-next"
        >
          <ChevronRight />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className="max-sm:hidden"
          aria-label="Last page"
          onClick={() => onPageChange(Math.max(totalPages, 1))}
          disabled={!canNext}
          data-testid="data-table-pagination-last"
        >
          <ChevronsRight />
        </Button>
      </div>
    </div>
  );
}
